import React, { useState } from 'react';
import { Auth } from 'aws-amplify';
import { 
  Box, TextField, Button, Typography, Paper, Alert, 
  Stepper, Step, StepLabel, CircularProgress
} from '@mui/material';

function LoginTest() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [cognitoUser, setCognitoUser] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [challengeName, setChallengeName] = useState(null);

  const steps = ['Sign In', 'Set New Password', 'Complete'];
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult('');
    
    try {
      // First sign in
      const user = await Auth.signIn(username, password);
      console.log('Sign in response:', user);
      
      // Check if there's a challenge
      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        console.log('New password required');
        setChallengeName('NEW_PASSWORD_REQUIRED');
        setCognitoUser(user);
        setActiveStep(1);
        setLoading(false);
        return;
      }
      
      // If no challenge, proceed with getting user data
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        console.log('Current authenticated user:', currentUser);
        
        const userInfo = {
          username: currentUser.username,
          attributes: currentUser.attributes || {},
          signInUserSession: currentUser.signInUserSession ? {
            accessToken: currentUser.signInUserSession.accessToken ? 'Present' : 'Not present',
            idToken: currentUser.signInUserSession.idToken ? 'Present' : 'Not present',
            refreshToken: currentUser.signInUserSession.refreshToken ? 'Present' : 'Not present'
          } : 'No session data'
        };
        
        setResult(`Login successful! User: ${JSON.stringify(userInfo, null, 2)}`);
        setActiveStep(2);
      } catch (userError) {
        console.error('Error getting current user:', userError);
        
        const userInfo = {
          username: user.username,
          challengeName: user.challengeName || 'None',
          signInUserSession: user.signInUserSession ? 'Present' : 'No session data'
        };
        
        setResult(`Login successful but limited data available: ${JSON.stringify(userInfo, null, 2)}`);
        setActiveStep(2);
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Complete the new password challenge
      const user = await Auth.completeNewPassword(
        cognitoUser,
        newPassword
      );
      
      console.log('Password challenge completed:', user);
      
      // Get the authenticated user data
      const currentUser = await Auth.currentAuthenticatedUser();
      
      const userInfo = {
        username: currentUser.username,
        attributes: currentUser.attributes || {},
        signInUserSession: currentUser.signInUserSession ? {
          accessToken: currentUser.signInUserSession.accessToken ? 'Present' : 'Not present',
          idToken: currentUser.signInUserSession.idToken ? 'Present' : 'Not present',
          refreshToken: currentUser.signInUserSession.refreshToken ? 'Present' : 'Not present'
        } : 'No session data'
      };
      
      setResult(`Authentication complete! Password changed successfully. User: ${JSON.stringify(userInfo, null, 2)}`);
      setActiveStep(2);
    } catch (error) {
      console.error('Error completing new password challenge:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const renderSignInForm = () => (
    <Box component="form" onSubmit={handleLogin} sx={{ mt: 2 }}>
      <TextField
        label="Username"
        fullWidth
        margin="normal"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        required
      />
      
      <TextField
        label="Password"
        type="password"
        fullWidth
        margin="normal"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />
      
      <Button 
        type="submit" 
        variant="contained" 
        fullWidth 
        sx={{ mt: 3, mb: 2 }}
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} /> : 'Sign In'}
      </Button>
    </Box>
  );
  
  const renderNewPasswordForm = () => (
    <Box component="form" onSubmit={handleNewPasswordSubmit} sx={{ mt: 2 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        You need to set a new password to continue.
      </Alert>
      
      <TextField
        label="New Password"
        type="password"
        fullWidth
        margin="normal"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        required
      />
      
      <TextField
        label="Confirm New Password"
        type="password"
        fullWidth
        margin="normal"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        required
      />
      
      <Button 
        type="submit" 
        variant="contained" 
        fullWidth 
        sx={{ mt: 3, mb: 2 }}
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} /> : 'Set New Password'}
      </Button>
    </Box>
  );
  
  const renderCompletionStep = () => (
    <Box sx={{ mt: 2 }}>
      <Alert severity="success" sx={{ mb: 2 }}>
        Authentication completed successfully!
      </Alert>
      
      <Button 
        variant="outlined" 
        fullWidth 
        sx={{ mt: 3, mb: 2 }}
        onClick={() => {
          setActiveStep(0);
          setUsername('');
          setPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setResult('');
          setError('');
          setCognitoUser(null);
          setChallengeName(null);
        }}
      >
        Sign In Again
      </Button>
    </Box>
  );
  
  const renderCurrentStep = () => {
    switch (activeStep) {
      case 0:
        return renderSignInForm();
      case 1:
        return renderNewPasswordForm();
      case 2:
        return renderCompletionStep();
      default:
        return renderSignInForm();
    }
  };
  
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
      <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 500 }}>
        <Typography variant="h5" component="h1" gutterBottom align="center">
          Cognito Authentication Test
        </Typography>
        
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {renderCurrentStep()}
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        
        {result && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem' }}>
              {result}
            </pre>
          </Alert>
        )}
      </Paper>
    </Box>
  );
}

export default LoginTest;
